import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {forkJoin} from 'rxjs';
import {DeviceConfigHttpService} from '../data-access/device-config-http.service';
import {
  ConfigScope,
  ConfigurationOverview,
  DeviceListItem,
  DeviceSettingDefinition,
  DeviceSettingObjectListItem,
  SettingsDocument
} from '../shared/models/device-config.model';
import {hasAscendingViolation} from '../shared/object-list-validation';
import {deviceConfigDenialMessage} from '../shared/device-config-denial';
import {ObjectListEditorComponent} from './object-list-editor.component';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';
import {formatInvariantDecimal, parseInvariantDecimal} from '../../../helpers/decimal-input-helper';

/** Tri-state of a boolean setting in the editor: inherit from the layer below, or override on/off. */
type BoolState = 'inherit' | 'on' | 'off';

/** A catalog setting bound into the editor: whether this layer overrides it, and the value if so. */
interface EditableField {
  def: DeviceSettingDefinition;
  /** Whether this layer explicitly sets the value (an unset key inherits from the layer below). */
  included: boolean;
  /** The edited value (defaults to the layer's value, else the catalog default). */
  value: unknown;
}

/**
 * Catalog-driven editor for the web-managed PoS app configuration (#108). The admin picks a **scope** — the
 * company baseline, a shard baseline, or a single device's override — and edits one field per catalog entry.
 * The editor renders generically from the served catalog, so a new backend setting appears automatically with
 * no front-end change. A layer only stores the settings it explicitly overrides (the "override this setting"
 * toggle); unset settings inherit from the layer below.
 *
 * Reached at `/company/device-config`, gated to ShardAdmin / CompanyOwner / PlatformOwner. Backed by
 * `/api/devices/v2026.6/configuration`. See docs/articles/devices/pos-app-configuration.md.
 */
@Component({
  selector: 'app-device-config',
  standalone: true,
  imports: [FormsModule, NgSelectModule, ObjectListEditorComponent],
  template: `
    <div class="px-4 sm:px-6 lg:px-8 py-4 absolute inset-0 overflow-y-auto">
      <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">

        <div>
          <h1 class="text-xl font-bold tracking-tight text-gray-900">Geräte-Konfiguration</h1>
          <p class="mt-1 text-sm text-gray-500">
            Konfiguriere die Kassen-App zentral: eine Grundeinstellung pro Unternehmen oder Standort, mit
            Übersteuerung je Gerät.
          </p>
        </div>

        <!-- Scope selector -->
        <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-3">
          <div class="inline-flex flex-wrap rounded-xl border border-gray-200 bg-white p-1 shadow-sm w-full sm:w-auto">
            @for (s of scopes; track s.value) {
              <button type="button" (click)="setScope(s.value)"
                      class="flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
                      [class.bg-theme-primary-50]="scope === s.value"
                      [class.text-theme-primary-700]="scope === s.value"
                      [class.shadow-sm]="scope === s.value"
                      [class.text-gray-500]="scope !== s.value">{{ s.label }}</button>
            }
          </div>

          @if (scope === 'shard') {
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="shard">Standort</label>
              <ng-select labelForId="shard" name="shard" class="ng-select cheqme-style text-sm" [clearable]="false"
                         placeholder="Bitte wählen…" [(ngModel)]="selectedShardId" (ngModelChange)="load()">
                @for (sh of shards; track sh.id) {
                  <ng-option [value]="sh.id">{{ sh.name }}</ng-option>
                }
              </ng-select>
            </div>
          }
          @if (scope === 'device') {
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="device">Gerät</label>
              <ng-select labelForId="device" name="device" class="ng-select cheqme-style text-sm" [clearable]="false"
                         placeholder="Bitte wählen…" [(ngModel)]="selectedClientId" (ngModelChange)="load()">
                @for (d of devices; track d.clientId) {
                  <ng-option [value]="d.clientId">{{ d.label }}</ng-option>
                }
              </ng-select>
              @if (devices.length === 0) {
                <p class="mt-2 text-xs text-gray-500">Keine Geräte registriert.</p>
              }
            </div>
          }
        </div>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error) {
          <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm" role="alert">
            <p class="text-sm font-semibold text-red-700">Die Konfiguration konnte nicht geladen werden.</p>
            <!-- #269: a denied read names its reason instead of leaving the admin with the generic sentence. -->
            @if (loadDenialReason) {
              <p class="mt-1 text-sm text-red-700">{{ loadDenialReason }}</p>
            }
            <button (click)="load()" class="mt-2 inline-flex items-center text-red-700 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">Erneut versuchen</button>
          </div>
        } @else if (needsSelection) {
          <p class="text-sm text-gray-500 italic py-8 border-2 border-dashed border-gray-100 rounded-2xl text-center">
            Bitte {{ scope === 'shard' ? 'einen Standort' : 'ein Gerät' }} auswählen.
          </p>
        } @else if (scope === 'overview' && overview) {
          <!-- Read-only overview: which layers diverge from the inherited configuration. -->
          <div class="flex flex-col gap-4">
            <div>
              <p class="text-base font-bold text-gray-800 tracking-tight mb-2">Unternehmen</p>
              @if (overview.companyBaselineSet) {
                <div class="p-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <p class="text-sm text-gray-600">Grundeinstellung gesetzt:
                    <span class="font-semibold text-gray-900">{{ overviewKeys(overview.companyKeys) }}</span></p>
                </div>
              } @else {
                <p class="text-sm text-gray-500 italic">Keine unternehmensweite Grundeinstellung.</p>
              }
            </div>

            <div>
              <p class="text-base font-bold text-gray-800 tracking-tight mb-2">Standorte mit eigener Einstellung</p>
              @if (overview.shards.length === 0) {
                <p class="text-sm text-gray-500 italic">Kein Standort weicht ab.</p>
              } @else {
                <ul class="flex flex-col gap-2">
                  @for (s of overview.shards; track s.shardId) {
                    <li class="flex items-center justify-between gap-3 p-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <span class="text-sm text-gray-700 min-w-0">
                        <span class="font-semibold text-gray-900">{{ s.shardName }}</span>
                        — {{ overviewKeys(s.keys) }}
                      </span>
                      <button type="button" (click)="editShard(s.shardId)"
                              class="shrink-0 text-theme-primary-700 bg-theme-primary-50 hover:bg-theme-primary-100 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200">Bearbeiten</button>
                    </li>
                  }
                </ul>
              }
            </div>

            <div>
              <p class="text-base font-bold text-gray-800 tracking-tight mb-2">Geräte mit eigener Einstellung</p>
              @if (overview.devices.length === 0) {
                <p class="text-sm text-gray-500 italic">Kein Gerät weicht ab.</p>
              } @else {
                <ul class="flex flex-col gap-2">
                  @for (d of overview.devices; track d.clientId) {
                    <li class="flex items-center justify-between gap-3 p-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <span class="text-sm text-gray-700 min-w-0">
                        <span class="font-semibold text-gray-900">{{ d.label }}</span>
                        — {{ overviewKeys(d.keys) }}
                      </span>
                      <button type="button" (click)="editDevice(d.clientId)"
                              class="shrink-0 text-theme-primary-700 bg-theme-primary-50 hover:bg-theme-primary-100 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200">Bearbeiten</button>
                    </li>
                  }
                </ul>
              }
            </div>
          </div>
        } @else {
          <!-- Fields, grouped by catalog group -->
          @for (group of groups; track group) {
            <div>
              <p class="text-base font-bold text-gray-800 tracking-tight mb-2">{{ group }}</p>
              <ul class="flex flex-col gap-2">
                @for (f of fieldsIn(group); track f.def.key) {
                  @if (f.def.type === 'objectlist') {
                    <!-- Ordered list of structured rows (e.g. the idle-warning levels). Its own list item, because
                         a row editor needs the full card width and never sits beside its label. -->
                    <li class="flex flex-col gap-3 p-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <label class="flex items-center gap-3 min-w-0">
                        <input type="checkbox" [checked]="f.included" (change)="toggle(f)"
                               [attr.aria-label]="'Übersteuern: ' + f.def.label"
                               class="h-4 w-4 rounded border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
                        <span class="text-sm font-semibold text-gray-900 truncate">{{ f.def.label }}</span>
                      </label>
                      <app-object-list-editor [fields]="f.def.itemFields ?? []" [items]="itemsOf(f)"
                                              [disabled]="!f.included"
                                              [strictlyAscendingBy]="f.def.strictlyAscendingBy ?? null"
                                              (itemsChange)="f.value = $event"/>
                    </li>
                  } @else {
                  <li class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    @if (f.def.type === 'multienum') {
                      <!-- Multi-select (set of options): the override checkbox decides whether this layer sets the
                           selection at all; the chips toggle which options are in it. An empty included selection
                           is meaningful (offer none) and distinct from inheriting. -->
                      <label class="flex items-center gap-3 min-w-0">
                        <input type="checkbox" [checked]="f.included" (change)="toggle(f)"
                               [attr.aria-label]="'Übersteuern: ' + f.def.label"
                               class="h-4 w-4 rounded border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
                        <span class="text-sm font-semibold text-gray-900 truncate">{{ f.def.label }}</span>
                      </label>
                      <div class="flex flex-wrap gap-1.5 sm:justify-end" role="group" [attr.aria-label]="f.def.label">
                        @for (o of f.def.options; track o; let i = $index) {
                          <button type="button" [disabled]="!f.included" (click)="toggleOption(f, o)"
                                  [attr.aria-pressed]="isOptionSelected(f, o)"
                                  class="px-3 py-1 text-sm font-medium rounded-lg border transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                  [class.bg-theme-primary-50]="isOptionSelected(f, o)"
                                  [class.border-theme-primary-200]="isOptionSelected(f, o)"
                                  [class.text-theme-primary-700]="isOptionSelected(f, o)"
                                  [class.border-gray-200]="!isOptionSelected(f, o)"
                                  [class.text-gray-500]="!isOptionSelected(f, o)">{{ optionLabel(f.def, i, o) }}</button>
                        }
                      </div>
                    } @else if (f.def.type === 'bool') {
                      <!-- Boolean: a single tri-state control (inherit / on / off) — captures both "override at
                           this layer" and the value, so there is no confusing checkbox-controlling-a-checkbox. -->
                      <span class="text-sm font-semibold text-gray-900 truncate">{{ f.def.label }}</span>
                      <div class="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm shrink-0"
                           role="group" [attr.aria-label]="f.def.label">
                        @for (opt of boolOptions; track opt.state) {
                          <button type="button" (click)="setBoolState(f, opt.state)"
                                  [attr.aria-pressed]="boolState(f) === opt.state"
                                  class="px-3 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                                  [class.bg-theme-primary-50]="boolState(f) === opt.state"
                                  [class.text-theme-primary-700]="boolState(f) === opt.state"
                                  [class.shadow-sm]="boolState(f) === opt.state"
                                  [class.text-gray-500]="boolState(f) !== opt.state">{{ opt.label }}</button>
                        }
                      </div>
                    } @else {
                      <label class="flex items-center gap-3 min-w-0">
                        <input type="checkbox" [checked]="f.included" (change)="toggle(f)"
                               [attr.aria-label]="'Übersteuern: ' + f.def.label"
                               class="h-4 w-4 rounded border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
                        <span class="text-sm font-semibold text-gray-900 truncate">{{ f.def.label }}</span>
                      </label>

                      <div class="sm:w-56 shrink-0">
                        @switch (f.def.type) {
                          @case ('int') {
                            <input type="number" [disabled]="!f.included" [attr.aria-label]="f.def.label"
                                   [ngModel]="$any(f.value)" (ngModelChange)="f.value = $event === null ? null : +$event"
                                   [name]="f.def.key"
                                   class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"/>
                          }
                          @case ('enum') {
                            <label class="sr-only" [for]="f.def.key + '-val'">{{ f.def.label }}</label>
                            <ng-select class="ng-select cheqme-style text-sm" [clearable]="false"
                                       [labelForId]="f.def.key + '-val'" [readonly]="!f.included"
                                       [ngModel]="$any(f.value)" (ngModelChange)="f.value = $event" [name]="f.def.key">
                              @for (o of f.def.options; track o; let i = $index) {
                                <ng-option [value]="o">{{ optionLabel(f.def, i, o) }}</ng-option>
                              }
                            </ng-select>
                          }
                          @case ('color') {
                            <!-- A single #RRGGBB value. The native picker is the control; the hex is echoed
                                 beside it so the stored value stays visible (and assertable). -->
                            <div class="flex items-center gap-2">
                              <input type="color" [disabled]="!f.included" [attr.aria-label]="f.def.label"
                                     [ngModel]="colorValue(f)" (ngModelChange)="f.value = $event" [name]="f.def.key"
                                     class="h-10 w-14 shrink-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-1 shadow-sm disabled:cursor-not-allowed disabled:opacity-40"/>
                              <span class="font-mono text-xs text-gray-600">{{ colorValue(f) }}</span>
                            </div>
                          }
                          @case ('decimal') {
                            <!-- A money/decimal JSON number (#204). Deliberately type=text rather than
                                 type=number, whose native comma/dot handling depends on the browser's OWN
                                 locale (not this app's) and is untestable in CI — the invariant parse/format
                                 helper is the one place a locale can leak in, and it never does (see
                                 helpers/decimal-input-helper.ts). A German admin typing "12,50" still ends up
                                 stored as the number 12.5, never the historical "0.4" to 4 de-DE bug. -->
                            <input type="text" inputmode="decimal" [disabled]="!f.included"
                                   [attr.aria-label]="f.def.label" [attr.aria-invalid]="decimalInvalid(f)"
                                   [ngModel]="decimalDisplayValue(f)" (ngModelChange)="onDecimalInput(f, $event)"
                                   [name]="f.def.key" placeholder="0,00"
                                   class="block w-full px-3 py-2 rounded-xl border bg-white shadow-sm focus:ring-1 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                                   [class.border-red-300]="decimalInvalid(f)"
                                   [class.focus:border-red-500]="decimalInvalid(f)"
                                   [class.focus:ring-red-500]="decimalInvalid(f)"
                                   [class.border-gray-200]="!decimalInvalid(f)"
                                   [class.focus:border-theme-primary-500]="!decimalInvalid(f)"
                                   [class.focus:ring-theme-primary-500]="!decimalInvalid(f)"/>
                            @if (decimalInvalid(f)) {
                              <p class="mt-2 flex items-center space-x-1 text-xs font-semibold text-red-500">
                                Bitte eine gültige, nicht-negative Zahl eingeben (z. B. 12,50).
                              </p>
                            }
                          }
                          @default {
                            <input type="text" [disabled]="!f.included" [attr.aria-label]="f.def.label"
                                   [ngModel]="$any(f.value)" (ngModelChange)="f.value = $event" [name]="f.def.key"
                                   class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"/>
                          }
                        }
                      </div>
                    }
                  </li>
                  }
                }
              </ul>
            </div>
          }

          <!-- Copy this layer's current settings to another layer (try on a device → promote to shard/company). -->
          <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-3">
            <div>
              <p class="text-base font-bold text-gray-800 tracking-tight">Einstellungen kopieren</p>
              <p class="text-xs text-gray-500">Kopiert die aktuellen Einstellungen dieser Ebene in eine andere Ebene (überschreibt sie dort).</p>
            </div>
            <div class="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div>
                <span class="block text-sm font-bold text-gray-700 mb-2 tracking-tight">Ziel</span>
                <div class="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm" role="group" aria-label="Zielebene">
                  @for (t of copyTargets; track t.value) {
                    <button type="button" (click)="copyTargetScope = t.value"
                            [attr.aria-pressed]="copyTargetScope === t.value"
                            class="px-3 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                            [class.bg-theme-primary-50]="copyTargetScope === t.value"
                            [class.text-theme-primary-700]="copyTargetScope === t.value"
                            [class.shadow-sm]="copyTargetScope === t.value"
                            [class.text-gray-500]="copyTargetScope !== t.value">{{ t.label }}</button>
                  }
                </div>
              </div>
              @if (copyTargetScope === 'shard') {
                <div class="grow">
                  <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="copy-shard">Standort</label>
                  <ng-select labelForId="copy-shard" name="copy-shard" class="ng-select cheqme-style text-sm"
                             [clearable]="false" placeholder="Bitte wählen…" [(ngModel)]="copyTargetShardId">
                    @for (sh of shards; track sh.id) { <ng-option [value]="sh.id">{{ sh.name }}</ng-option> }
                  </ng-select>
                </div>
              }
              @if (copyTargetScope === 'device') {
                <div class="grow">
                  <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="copy-device">Gerät</label>
                  <ng-select labelForId="copy-device" name="copy-device" class="ng-select cheqme-style text-sm"
                             [clearable]="false" placeholder="Bitte wählen…" [(ngModel)]="copyTargetClientId">
                    @for (d of devices; track d.clientId) { <ng-option [value]="d.clientId">{{ d.label }}</ng-option> }
                  </ng-select>
                </div>
              }
              <button type="button" (click)="copy()" [disabled]="!canCopy || saving || hasBlockingViolation"
                      class="inline-flex items-center justify-center px-4 py-2.5 text-theme-primary-700 bg-theme-primary-50 hover:bg-theme-primary-100 rounded-2xl font-semibold transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed">
                Kopieren
              </button>
            </div>
          </div>

          @if (actionDenialReason) {
            <!-- #269: the denial reason from the last save/copy/reset. The toast auto-dismisses after 3s; the
                 reason a write is refused is a standing fact about this layer, so it also stays on screen until
                 the next attempt. -->
            <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm" role="alert">
              <p class="text-sm font-semibold text-red-700">{{ actionDenialReason }}</p>
            </div>
          }

          @if (hasBlockingViolation) {
            <!-- The backend answers 422 on a non-ascending list; say so up front instead of after a failed save. -->
            <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm" role="alert">
              <p class="text-sm font-semibold text-red-700">
                {{ blockingViolationLabels }}: die Werte müssen von Zeile zu Zeile streng aufsteigen.
              </p>
              <p class="mt-1 text-xs text-red-700">Speichern ist erst nach dem Korrigieren möglich.</p>
            </div>
          }

          <div class="flex items-center justify-between gap-3">
            <!-- Reset is only meaningful for a shard/device layer (clears its overrides so it inherits again);
                 the company baseline has nothing to inherit from. -->
            @if (scope !== 'company') {
              <button type="button" (click)="reset()" [disabled]="saving"
                      class="inline-flex items-center justify-center px-4 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-2xl font-semibold transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed">
                Auf vererbt zurücksetzen
              </button>
            } @else {
              <span></span>
            }
            <button type="button" (click)="save()" [disabled]="saving || hasBlockingViolation"
                    class="inline-flex items-center justify-center px-5 py-2.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">
              Speichern
            </button>
          </div>
        }
      </div>
    </div>
  `
})
export class DeviceConfigComponent implements OnInit {
  protected readonly scopes: { value: ConfigScope; label: string }[] = [
    {value: 'company', label: 'Unternehmen'},
    {value: 'shard', label: 'Standort'},
    {value: 'device', label: 'Gerät'},
    {value: 'overview', label: 'Übersicht'}
  ];

  /** Loaded when the 'overview' scope is active: which shards/devices diverge from the inherited config. */
  protected overview: ConfigurationOverview | null = null;

  // Copy the current layer's settings to another layer (e.g. try on a device, then promote to the shard).
  protected copyTargetScope: 'company' | 'shard' | 'device' = 'shard';
  protected copyTargetShardId = '';
  protected copyTargetClientId = '';

  protected loading = true;
  protected error = false;
  protected saving = false;

  /**
   * #269: the structured reason a *read* was denied (`403` + `{ code, description }`), rendered inside the load
   * error banner. Null for any other failure — the banner then shows only its generic sentence, as before.
   * Public for tests.
   */
  loadDenialReason: string | null = null;

  /**
   * #269: the structured reason the last *write* (save / copy / reset) was denied. Kept on screen (the toast
   * auto-dismisses) until the next attempt, because the reason is a standing property of the selected layer, not
   * a transient event. Public for tests.
   */
  actionDenialReason: string | null = null;

  protected scope: ConfigScope = 'company';
  /** Bound by the pickers; public for templates + tests. */
  selectedShardId = '';
  selectedClientId = '';

  protected catalog: DeviceSettingDefinition[] = [];
  protected devices: DeviceListItem[] = [];
  /** Public for templates + tests. */
  fields: EditableField[] = [];

  constructor(private http: DeviceConfigHttpService, private alert: GlobalAlertService) {
  }

  ngOnInit(): void {
    this.loading = true;
    forkJoin({catalog: this.http.catalog(), devices: this.http.devices()}).subscribe({
      next: ({catalog, devices}) => {
        this.catalog = catalog;
        this.devices = devices;
        this.load();
      },
      error: err => {
        this.error = true;
        this.loadDenialReason = this.denialReasonOf(err);
        this.loading = false;
      }
    });
  }

  /**
   * The structured denial reason of a failed request (#269), or null when the failure was not a `403` — the
   * callers then keep their existing generic message. Only `403` is treated this way on purpose: a `422`
   * (invalid configuration) and a `5xx` are not permission problems and already have their own wording.
   */
  private denialReasonOf(error: unknown): string | null {
    return (error as { status?: number } | null)?.status === 403
      ? deviceConfigDenialMessage(error)
      : null;
  }

  /**
   * The message for a failed write: the #269 denial reason when the server refused the operation, the existing
   * wording otherwise. `generic` is the action's own fallback sentence ("… konnte nicht gespeichert werden.").
   */
  private failureMessage(error: unknown, generic: string): string {
    return this.denialReasonOf(error)
      ?? ((error as { status?: number } | null)?.status === 422 ? 'Ungültige Konfiguration.' : generic);
  }

  /** Distinct shards (id + alias) derived from the registered devices (no separate shard endpoint needed). */
  get shards(): { id: string; name: string }[] {
    const byId = new Map<string, string>();
    for (const d of this.devices) if (!byId.has(d.shardId)) byId.set(d.shardId, d.shardName);
    return [...byId].map(([id, name]) => ({id, name}));
  }

  /** Whether the chosen scope still needs a shard/device pick before a layer can be loaded. */
  get needsSelection(): boolean {
    return (this.scope === 'shard' && !this.selectedShardId) ||
      (this.scope === 'device' && !this.selectedClientId);
  }

  /** Catalog groups in first-seen order. */
  get groups(): string[] {
    return [...new Set(this.catalog.map(d => d.group))];
  }

  fieldsIn(group: string): EditableField[] {
    return this.fields.filter(f => f.def.group === group);
  }

  setScope(scope: ConfigScope): void {
    this.scope = scope;
    if (scope === 'overview') this.loadOverview();
    else this.load();
  }

  /** Load the read-only overview of which shards/devices override settings. */
  loadOverview(): void {
    this.loading = true;
    this.error = false;
    this.loadDenialReason = null;
    this.http.overview().subscribe({
      next: o => {
        this.overview = o;
        this.loading = false;
      },
      error: err => {
        this.error = true;
        this.loadDenialReason = this.denialReasonOf(err);
        this.loading = false;
      }
    });
  }

  /** The human label for a catalog key (falls back to the raw key if the catalog hasn't loaded it). */
  keyLabel(key: string): string {
    return this.catalog.find(d => d.key === key)?.label ?? key;
  }

  /** Comma-joined human labels for a set of overridden keys (for the overview rows). */
  overviewKeys(keys: string[]): string {
    return keys.map(k => this.keyLabel(k)).join(', ');
  }

  /** Jump from the overview into editing a specific shard baseline. */
  editShard(shardId: string): void {
    this.scope = 'shard';
    this.selectedShardId = shardId;
    this.load();
  }

  /** Jump from the overview into editing a specific device override. */
  editDevice(clientId: string): void {
    this.scope = 'device';
    this.selectedClientId = clientId;
    this.load();
  }

  load(): void {
    // A layer switch invalidates the previous layer's denial reasons — the whole point of naming the reason is
    // that it is specific to the layer being edited.
    this.actionDenialReason = null;
    this.loadDenialReason = null;
    if (this.needsSelection) {
      this.loading = false;
      this.error = false;
      this.fields = [];
      return;
    }
    this.loading = true;
    this.error = false;

    const request = this.scope === 'company' ? this.http.getBaseline()
      : this.scope === 'shard' ? this.http.getBaseline(this.selectedShardId)
        : this.http.getOverride(this.selectedClientId);

    request.subscribe({
      next: layer => {
        this.fields = this.catalog.map(def => {
          const has = Object.prototype.hasOwnProperty.call(layer.settings ?? {}, def.key);
          const raw = has ? layer.settings[def.key] : def.default;
          // Clone array values (multienum, objectlist) so editing never mutates the shared catalog default or the
          // loaded layer. Objectlist rows are objects, so the elements are cloned too — a shallow copy of the
          // outer array would still hand the catalog default's row objects to the editor.
          return {def, included: has, value: Array.isArray(raw) ? raw.map(item => this.cloneValue(item)) : raw};
        });
        this.loading = false;
      },
      error: err => {
        this.error = true;
        this.loadDenialReason = this.denialReasonOf(err);
        this.loading = false;
      }
    });
  }

  toggle(field: EditableField): void {
    field.included = !field.included;
  }

  /** One level of defensive cloning for a loaded array element (a multienum string, or an objectlist row). */
  private cloneValue(value: unknown): unknown {
    return value !== null && typeof value === 'object' ? {...value as object} : value;
  }

  /** Whether a multienum option is currently in this field's selected set. */
  isOptionSelected(field: EditableField, option: string): boolean {
    return Array.isArray(field.value) && field.value.includes(option);
  }

  /**
   * The display label for the option at {@link index}, falling back to the raw option value when the def has no
   * label for it (its `optionLabels` may be empty or shorter than `options`). The fallback lives here rather than
   * in the template because `optionLabels[i]` is typed `string` (array index access), so a template-side `?.`/`??`
   * on it is flagged as redundant (NG8107/NG8102) even though the value is genuinely `undefined` out of bounds.
   */
  optionLabel(def: DeviceSettingDefinition, index: number, option: string): string {
    return def.optionLabels[index] ?? option;
  }

  /**
   * Toggle a multienum option in/out of the field's selected set (immutably, so the shared catalog default is
   * never mutated). The option keys are kept in the catalog's declared order for a stable, deterministic payload.
   */
  toggleOption(field: EditableField, option: string): void {
    if (!field.included) return;
    const current = new Set(Array.isArray(field.value) ? (field.value as string[]) : []);
    if (current.has(option)) current.delete(option); else current.add(option);
    field.value = field.def.options.filter(o => current.has(o));
  }

  /**
   * The `objectlist` rows of a field, as the row editor wants them. A layer document is untyped JSON, so a
   * non-array value (a corrupted/legacy layer) degrades to an empty list rather than throwing in the template.
   */
  itemsOf(field: EditableField): DeviceSettingObjectListItem[] {
    return Array.isArray(field.value) ? field.value as DeviceSettingObjectListItem[] : [];
  }

  /**
   * The value handed to an `<input type="color">`, which only accepts a full `#RRGGBB`. An absent or malformed
   * value would silently render as black, so it falls back to the catalog default when that is a valid colour,
   * and to a visible neutral otherwise.
   */
  colorValue(field: EditableField): string {
    const hex = /^#[0-9a-fA-F]{6}$/;
    if (typeof field.value === 'string' && hex.test(field.value)) return field.value;
    if (typeof field.def.default === 'string' && hex.test(field.def.default)) return field.def.default;
    return '#808080';
  }

  /**
   * The overridden `objectlist` fields whose rows break their catalog-declared strictly-ascending rule. The
   * backend rejects such a document with a 422, so the editor blocks the save locally and names the offenders —
   * a bare "Ungültige Konfiguration" toast after the fact is useless in a multi-row editor.
   */
  get ascendingViolations(): EditableField[] {
    return this.fields.filter(f => f.included && f.def.type === 'objectlist'
      && hasAscendingViolation(this.itemsOf(f), f.def.strictlyAscendingBy ?? null));
  }

  /** Whether an unsaveable list is currently being edited (drives the save/copy disabled state). */
  get hasBlockingViolation(): boolean {
    return this.ascendingViolations.length > 0 || this.fields.some(f => f.included && this.decimalInvalid(f));
  }

  /** Comma-joined labels of the settings that currently block a save. */
  get blockingViolationLabels(): string {
    return this.ascendingViolations.map(f => f.def.label).join(', ');
  }

  // ---- decimal (#204) -----------------------------------------------------------------------------------

  /**
   * The text shown in a `decimal` field's input. While the field still holds the JSON number loaded from the
   * layer/catalog default, it is formatted invariantly; once the admin has typed into the box, `field.value`
   * holds their raw (possibly still-being-composed, e.g. "12,") text verbatim so the control never fights the
   * user mid-keystroke — {@link toWireValue} is what parses it back to a number at save time.
   */
  decimalDisplayValue(field: EditableField): string {
    return typeof field.value === 'string' ? field.value : formatInvariantDecimal(field.value as number | null);
  }

  /** Stores the admin's raw input verbatim; parsing happens on read ({@link decimalInvalid}, {@link toWireValue}). */
  onDecimalInput(field: EditableField, raw: string): void {
    field.value = raw;
  }

  /** Whether a `decimal` field's current (non-empty) text fails to parse — blocks the save, see {@link hasBlockingViolation}. */
  decimalInvalid(field: EditableField): boolean {
    if (field.def.type !== 'decimal' || typeof field.value !== 'string') return false;
    return field.value.trim() !== '' && parseInvariantDecimal(field.value) === null;
  }

  /**
   * The value actually written to the settings document: every type round-trips through as-is EXCEPT
   * `decimal`, whose in-editor value may still be the admin's raw typed string — this is the one place it is
   * parsed back into the JSON number the backend's `Decimal` validation requires (never a string). An empty or
   * still-invalid decimal is saved as `0` ("no default"/"no limit" — see the catalog docs); {@link
   * hasBlockingViolation} already blocks the save button while a *non-empty* value is unparseable, so this
   * fallback is only ever reached for an intentionally cleared field.
   */
  private toWireValue(field: EditableField): unknown {
    if (field.def.type !== 'decimal') return field.value;
    if (typeof field.value === 'number') return field.value;
    if (typeof field.value === 'string') return parseInvariantDecimal(field.value) ?? 0;
    return 0;
  }

  /** Target layers a copy can write to. */
  protected readonly copyTargets: { value: 'company' | 'shard' | 'device'; label: string }[] = [
    {value: 'company', label: 'Unternehmen'},
    {value: 'shard', label: 'Standort'},
    {value: 'device', label: 'Gerät'}
  ];

  /** The tri-state options for a boolean setting (inherit from the layer below / on / off). */
  protected readonly boolOptions: { state: BoolState; label: string }[] = [
    {state: 'inherit', label: 'Vererbt'},
    {state: 'on', label: 'An'},
    {state: 'off', label: 'Aus'}
  ];

  /** Current tri-state of a boolean field: not overridden → inherit, else on/off from its value. */
  boolState(field: EditableField): BoolState {
    if (!field.included) return 'inherit';
    return field.value === true ? 'on' : 'off';
  }

  /** Apply a tri-state choice: inherit clears the override; on/off set it at this layer. */
  setBoolState(field: EditableField, state: BoolState): void {
    if (state === 'inherit') {
      field.included = false;
      return;
    }
    field.included = true;
    field.value = state === 'on';
  }

  /** The settings document to persist: only the fields the admin chose to override at this layer. */
  private buildSettings(): SettingsDocument {
    const settings: SettingsDocument = {};
    for (const f of this.fields)
      if (f.included) settings[f.def.key] = this.toWireValue(f);
    return settings;
  }

  save(): void {
    if (this.needsSelection || this.saving || this.hasBlockingViolation) return;
    this.saving = true;
    this.actionDenialReason = null;
    const settings = this.buildSettings();

    const request = this.scope === 'company' ? this.http.putBaseline(settings)
      : this.scope === 'shard' ? this.http.putBaseline(settings, this.selectedShardId)
        : this.http.putOverride(this.selectedClientId, settings);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.alert.createAndShow('Gespeichert', 'Die Konfiguration wurde gespeichert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.saving = false;
        // #269: a denied save now names *why* (structured 403 body) instead of the anonymous permission sentence.
        const message = this.failureMessage(err, 'Die Konfiguration konnte nicht gespeichert werden.');
        this.actionDenialReason = this.denialReasonOf(err);
        this.alert.createAndShow('Fehler', message, AlertLevel.error, 3000);
      }
    });
  }

  /** Whether the copy target is fully selected (and not the very layer we're copying from). */
  get canCopy(): boolean {
    if (this.copyTargetScope === 'shard' && !this.copyTargetShardId) return false;
    if (this.copyTargetScope === 'device' && !this.copyTargetClientId) return false;
    // Copying a layer onto itself is a no-op; disallow to avoid confusion.
    if (this.copyTargetScope === 'company' && this.scope === 'company') return false;
    if (this.copyTargetScope === 'shard' && this.scope === 'shard' && this.copyTargetShardId === this.selectedShardId) return false;
    if (this.copyTargetScope === 'device' && this.scope === 'device' && this.copyTargetClientId === this.selectedClientId) return false;
    return true;
  }

  /**
   * Copy the current layer's settings to another layer — e.g. configure a device, confirm it works, then promote
   * it to the shard baseline (device → shard), or company-wide. Uses the current (possibly edited) overrides;
   * confirmed first, and the target's existing settings are overwritten.
   */
  copy(): void {
    if (!this.canCopy || this.saving || this.hasBlockingViolation) return;
    const settings = this.buildSettings();
    const target = this.copyTargetScope === 'company' ? 'das Unternehmen'
      : this.copyTargetScope === 'shard' ? 'den Standort' : 'das Gerät';
    if (!confirm(`Aktuelle Einstellungen nach ${target} kopieren? Bestehende Einstellungen dort werden überschrieben.`)) return;

    this.saving = true;
    this.actionDenialReason = null;
    const request = this.copyTargetScope === 'company' ? this.http.putBaseline(settings)
      : this.copyTargetScope === 'shard' ? this.http.putBaseline(settings, this.copyTargetShardId)
        : this.http.putOverride(this.copyTargetClientId, settings);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.alert.createAndShow('Kopiert', 'Die Einstellungen wurden in die Zielebene kopiert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.saving = false;
        // #269: copying into the company baseline is the same company-wide write as saving it — same reason, so
        // the same structured code and the same named message.
        const message = this.failureMessage(err, 'Kopieren fehlgeschlagen.');
        this.actionDenialReason = this.denialReasonOf(err);
        this.alert.createAndShow('Fehler', message, AlertLevel.error, 3000);
      }
    });
  }

  /**
   * Reset this shard/device layer so it inherits everything again — writes an empty layer (a new version that
   * overrides nothing). Confirmed first; not offered for the company baseline (nothing below it to inherit).
   */
  reset(): void {
    if (this.scope === 'company' || this.needsSelection || this.saving) return;
    const what = this.scope === 'shard' ? 'diesen Standort' : 'dieses Gerät';
    if (!confirm(`Alle Einstellungen für ${what} auf „vererbt" zurücksetzen?`)) return;

    this.saving = true;
    this.actionDenialReason = null;
    const request = this.scope === 'shard'
      ? this.http.putBaseline({}, this.selectedShardId)
      : this.http.putOverride(this.selectedClientId, {});

    request.subscribe({
      next: () => {
        this.saving = false;
        this.load(); // refresh: every field now shows inherited
        this.alert.createAndShow('Zurückgesetzt', 'Alle Einstellungen werden wieder vererbt.', AlertLevel.success, 2000);
      },
      error: err => {
        this.saving = false;
        // #269: a reset is a write of an empty layer, so it hits the same guards and gets the same named reason.
        const message = this.failureMessage(err, 'Zurücksetzen fehlgeschlagen.');
        this.actionDenialReason = this.denialReasonOf(err);
        this.alert.createAndShow('Fehler', message, AlertLevel.error, 3000);
      }
    });
  }
}
